import { useCallback } from 'react';
import type { FormInstance } from 'antd/es/form';
import type { FormErrorFields } from 'utils/error-handlers';

const FALLBACK_ERROR_STEP = 0;

const STEP_FIELD_GROUPS: Record<number, string[]> = {
    0: [
        'medication_name',
        'release_form',
        'international_name_id',
        'manufacturer_id',
    ],
    1: [
        'certificate',
        'applicant_id',
        'notification_email',
        'procedure_id',
        'procedure_desc',
    ],
    2: ['is_electronic_form', 'is_ectd', 'is_mibp'],
    3: ['files'],
    4: ['reg_num', 'state_id'],
    5: ['reg_date', 'reject_reason'],
};

const FIELD_STEP_MAP = Object.entries(STEP_FIELD_GROUPS).reduce(
    (acc, [step, fields]) => {
        fields.forEach(field => {
            acc[field] = Number(step);
        });
        return acc;
    },
    {} as Record<string, number>
);

const clampStep = (step: number, totalSteps: number) => {
    const upperBound = Math.max(totalSteps - 1, 0);
    return Math.max(0, Math.min(step, upperBound));
};

const resolveFieldFromName = (name: (string | number)[] | string | number) => {
    if (Array.isArray(name)) {
        return name.find(part => typeof part === 'string');
    }

    return typeof name === 'string' ? name : undefined;
};

export const resolveStepFromErrorFields = (
    errors?: FormErrorFields | { name: (string | number)[] }[],
    fieldStepMap: Record<string, number> = FIELD_STEP_MAP
): number | undefined => {
    if (!errors?.length) return undefined;

    for (const field of errors) {
        const candidate = resolveFieldFromName(field.name);
        if (candidate) {
            return fieldStepMap[candidate] ?? FALLBACK_ERROR_STEP;
        }
    }

    return undefined;
};

interface UseStepNavigatorOptions {
    currentStep: number;
    totalSteps: number;
    setStep: (step: number) => void;
    form?: FormInstance;
    requiredFieldsMap?: Record<number, string[]>;
    onBeforeStepChange?: () => void;
    onValidationWarning?: (message: string) => void;
    validationMessage?: string;
    fieldStepMap?: Record<string, number>;
}

export const useStepNavigator = ({
    currentStep,
    totalSteps,
    setStep,
    form,
    requiredFieldsMap,
    onBeforeStepChange,
    onValidationWarning,
    validationMessage,
    fieldStepMap = FIELD_STEP_MAP,
}: UseStepNavigatorOptions) => {
    const goToStep = useCallback(
        (step: number) => {
            setStep(clampStep(step, totalSteps));
        },
        [setStep, totalSteps]
    );

    const validateCurrentStep = useCallback(async () => {
        if (!form || !requiredFieldsMap) return;

        const requiredFields = requiredFieldsMap[currentStep];
        if (!requiredFields || requiredFields.length === 0) {
            return;
        }

        try {
            await form.validateFields(requiredFields);
        } catch (error) {
            if (validationMessage && onValidationWarning) {
                onValidationWarning(validationMessage);
            }
        }
    }, [currentStep, form, requiredFieldsMap, onValidationWarning, validationMessage]);

    const goNext = useCallback(async () => {
        await validateCurrentStep();
        onBeforeStepChange?.();
        goToStep(currentStep + 1);
    }, [currentStep, goToStep, onBeforeStepChange, validateCurrentStep]);

    const goPrevious = useCallback(() => {
        onBeforeStepChange?.();
        goToStep(currentStep - 1);
    }, [currentStep, goToStep, onBeforeStepChange]);

    const redirectToErrorStep = useCallback(
        (errors?: FormErrorFields | { name: (string | number)[] }[]) => {
            const stepFromErrors = resolveStepFromErrorFields(
                errors,
                fieldStepMap
            );
            if (typeof stepFromErrors === 'number') {
                goToStep(stepFromErrors);
            }
        },
        [goToStep, fieldStepMap]
    );

    return {
        goNext,
        goPrevious,
        goToStep,
        redirectToErrorStep,
    };
};

export { STEP_FIELD_GROUPS, FIELD_STEP_MAP, FALLBACK_ERROR_STEP };
